import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { userRewardApi } from '../../api/reward.api';
import type {
  AttendanceStatusResponse,
  AttendanceCheckResponse,
} from '../../types/Reward.api.type';
import { Button } from '../ui/button';

/** 요일 헤더 */
const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const AttendanceTab = () => {
  const queryClient = useQueryClient();

  /** 출석 완료 모달 상태 */
  const [attendanceModal, setAttendanceModal] = useState<{
    open: boolean;
    earnedPieces: number;
    streakBonusPieces: number;
    streakCount: number;
    /** M-024: 첫 출석 토스포인트 지급액(원). 0이면 조각 경로 */
    tossPointAmount: number;
    /** M-024: NOT_TARGET | SUCCESS | DUMMY | FAILED */
    rewardStatus: string;
  }>({ open: false, earnedPieces: 0, streakBonusPieces: 0, streakCount: 0, tossPointAmount: 0, rewardStatus: 'NOT_TARGET' });

  /** 광고 보너스 가능 여부 (이번 세션 출석 완료 후 활성화) */
  const [adBonusAvailable, setAdBonusAvailable] = useState(false);

  /** 출석 현황 조회 */
  const { data: status } = useQuery<AttendanceStatusResponse>({
    queryKey: ['attendanceStatus'],
    queryFn: () => userRewardApi.getAttendanceStatus().then((res) => res.data.results),
  });

  /** 출석 체크 */
  const { mutate: checkAttendance, isPending: isCheckPending } = useMutation({
    mutationFn: () => userRewardApi.checkAttendance(),
    onSuccess: (res) => {
      const data = res.data.results as AttendanceCheckResponse;
      setAttendanceModal({
        open: true,
        earnedPieces: data.earnedPieces,
        streakBonusPieces: data.streakBonusPieces,
        streakCount: data.streakCount,
        tossPointAmount: data.tossPointAmount ?? 0,
        rewardStatus: data.rewardStatus ?? 'NOT_TARGET',
      });
      setAdBonusAvailable(true);
      queryClient.invalidateQueries({ queryKey: ['attendanceStatus'] });
      queryClient.invalidateQueries({ queryKey: ['pointBalance'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || '출석 처리에 실패했어요.';
      toast.error(message);
    },
  });

  /** 연속 유지 (웹: confirm 다이얼로그 + 더미 광고) */
  const { mutate: saveStreak, isPending: isStreakSavePending } = useMutation({
    mutationFn: () => userRewardApi.saveAttendanceStreak(),
    onSuccess: () => {
      toast.success('연속 출석을 유지했어요! 오늘 출석하면 연속기록이 이어져요.');
      queryClient.invalidateQueries({ queryKey: ['attendanceStatus'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || '연속 유지에 실패했어요.';
      toast.error(message);
    },
  });

  /** 광고 시청 후 streak 유지 (웹: 더미 광고 = confirm) */
  const handleStreakSave = () => {
    const confirmed = window.confirm('[테스트] 광고를 시청하시겠습니까?\n확인 시 연속 출석을 유지합니다.');
    if (confirmed) {
      saveStreak();
    }
  };

  /** 광고보고 조각 더 받기 (웹: 더미 광고) */
  const handleAdBonus = () => {
    const confirmed = window.confirm('[테스트] 광고를 시청하시겠습니까?\n확인 시 추가 조각을 받습니다.');
    if (confirmed) {
      toast.success('조각을 추가로 받았어요! (웹 더미 광고)');
      setAdBonusAvailable(false);
    }
  };

  /** 출석하기 클릭 (연속유지 가능한데 출석 누르면 confirm) */
  const handleCheckAttendance = () => {
    if (status?.canSaveStreak) {
      const useStreakSave = window.confirm(
        '연속 출석을 유지할 수 있어요!\n\n확인: 광고보고 연속 유지\n취소: 그냥 출석하기 (1일부터 시작)',
      );
      if (useStreakSave) {
        handleStreakSave();
      } else {
        checkAttendance();
      }
    } else {
      checkAttendance();
    }
  };

  /** 달력 그리드 생성 */
  const buildCalendarDays = (year: number, month: number): (number | null)[] => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const todayDate = today.getDate();
  const calendarDays = buildCalendarDays(year, month);

  const attendedDates = new Set(
    (status?.monthAttendance ?? []).map((d) => new Date(d + 'T00:00:00').getDate()),
  );

  if (!status) return null;

  const nextBonus = status.nextBonuses?.[0];

  return (
    <div className="flex flex-col gap-6">
      {/* 연속 출석 뱃지 */}
      {status.currentStreak > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <p className="text-lg font-bold text-gray-900">
            {status.currentStreak}일 연속 출석 중!
          </p>
          {nextBonus && (
            <p className="text-sm text-blue-600 mt-1">
              {nextBonus.daysLeft}일 더 출석하면 +{nextBonus.pieces}조각 추가 보상!
            </p>
          )}
        </div>
      )}

      {/* 달력 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm text-gray-600 mb-3">
          {year}년 {month + 1}월
        </p>
        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAY_LABELS.map((day) => (
            <div key={day} className="text-center">
              <span className="text-xs text-gray-500">{day}</span>
            </div>
          ))}
        </div>
        {/* 날짜 그리드 */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, idx) => {
            if (day === null) return <div key={`empty-${idx}`} className="h-9" />;
            const isToday = day === todayDate;
            const isAttended = attendedDates.has(day);
            return (
              <div
                key={day}
                className={`h-9 w-9 mx-auto rounded-full flex items-center justify-center text-sm ${
                  isAttended
                    ? 'bg-blue-500 text-white'
                    : isToday
                      ? 'border-2 border-blue-500 text-blue-600 font-bold'
                      : 'text-gray-700'
                }`}
              >
                {isAttended ? '✓' : day}
              </div>
            );
          })}
        </div>
      </div>

      {/* 출석 안내 */}
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold text-blue-600 w-5">1</span>
          <span className="text-sm text-gray-800">출석하면 조각을 받을 수 있어요</span>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold text-blue-600 w-5">2</span>
          <span className="text-sm text-gray-800">연속 출석하면 더 많은 조각 보상을 받을 수 있어요</span>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold text-blue-600 w-5">3</span>
          <span className="text-sm text-gray-800">광고를 보면 추가 조각을 받을 수 있어요</span>
        </div>
      </div>

      {/* S6-20: 연속 유지 버튼 — 출석 버튼 위에 배치 + 색상 강조 */}
      {status.canSaveStreak && (
        <Button
          className="w-full"
          onClick={handleStreakSave}
          disabled={isStreakSavePending}
        >
          {isStreakSavePending ? '유지 중...' : '어제 놓쳤어요! 광고보고 연속 유지하기'}
        </Button>
      )}

      {/* 출석 버튼 */}
      <Button
        className="w-full"
        variant={status.canSaveStreak && !status.attendedToday ? 'outline' : 'default'}
        disabled={status.attendedToday || isCheckPending}
        onClick={handleCheckAttendance}
      >
        {status.attendedToday ? '오늘 출석 완료' : '오늘 출석하기'}
      </Button>

      {/* 출석 후 광고보고 조각 더 받기 */}
      {status.attendedToday && adBonusAvailable && (
        <Button
          className="w-full"
          variant="outline"
          onClick={handleAdBonus}
        >
          광고보고 조각 더 받기
        </Button>
      )}

      {/* 안내사항 */}
      <div className="mt-4 bg-gray-50 rounded-lg p-4">
        <ul className="text-xs text-gray-500 space-y-1.5 list-disc list-inside">
          <li>매일 출석하면 조각이 적립돼요.</li>
          <li>연속 출석 시 추가 보너스 조각을 받을 수 있어요.</li>
          <li>하루 놓쳐도 광고를 보면 연속 출석을 유지할 수 있어요.</li>
          <li>2일 이상 빠지면 연속 출석이 초기화돼요.</li>
        </ul>
      </div>

      {/* 출석 완료 모달 */}
      {attendanceModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-80 text-center shadow-xl">
            <h3 className="text-lg font-bold text-blue-600 mb-2">
              {/* M-024 표시 매트릭스 (CP1 5R P1-3 — BE 실제 지급과 문구 일치)
                  SUCCESS       : 토스포인트 지급됨
                  NOT_TARGET/없음: 조각 지급됨
                  그 외(DUMMY/FAILED/UNCERTAIN/ALREADY_CLAIMED)
                                : 🔴 조각도 토스포인트도 지급되지 않았다 */}
              {attendanceModal.rewardStatus === 'SUCCESS'
                ? `첫 출석 축하해요! 토스포인트 ${attendanceModal.tossPointAmount}원을 받았어요`
                : attendanceModal.rewardStatus === 'NOT_TARGET' || !attendanceModal.rewardStatus
                  ? `오늘 출석 완료! ${attendanceModal.earnedPieces}조각을 받았어요`
                  : '출석은 완료됐어요. 토스포인트는 지급되지 않았어요'}
            </h3>
            {attendanceModal.streakBonusPieces > 0 && (
              <p className="text-sm text-orange-500 mb-1">
                +{attendanceModal.streakBonusPieces}조각 연속 보너스!
              </p>
            )}
            {attendanceModal.streakCount > 1 && (
              <p className="text-xs text-gray-600 mb-4">
                {attendanceModal.streakCount}일 연속 출석 중
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => setAttendanceModal((prev) => ({ ...prev, open: false }))}
              >
                확인
              </Button>
              {adBonusAvailable && (
                <Button
                  className="flex-1"
                  onClick={() => {
                    setAttendanceModal((prev) => ({ ...prev, open: false }));
                    handleAdBonus();
                  }}
                >
                  광고보고 조각 더 받기
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTab;
